import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle, RotateCcw, Lock, Send } from "lucide-react";
import { Button, Badge } from "@tabipla/ui";
import type {
  SessionResponseDto,
  GroupMemberResponseDto,
  ExpenseResponseDto,
  MemberBalanceDto,
  SessionImageDto,
} from "@tabipla/shared";
import { BalanceSummary } from "./BalanceSummary";
import { SettlementView } from "./SettlementView";
import { ExpenseList } from "./ExpenseList";
import { ExpenseForm } from "./ExpenseForm";
import { ReceiptUploader } from "./ReceiptUploader";

interface SessionDetailPresenterProps {
  // Data passed from parent
  groupId: string;
  session: SessionResponseDto;
  members: GroupMemberResponseDto[];
  currentUserId: string;
  currentUserType: "user" | "guest";
  canEdit: boolean;
  simplified: boolean;

  // Data fetched by container
  expenses: ExpenseResponseDto[];
  balances: MemberBalanceDto[];
  sessionImages: SessionImageDto[];

  // UI State
  showExpenseDialog: boolean;
  editingExpense: ExpenseResponseDto | null;
  isInputting: boolean;
  isSettling: boolean;
  isCompleted: boolean;
  canModify: boolean;

  // Loading states
  isStartingSettling: boolean;
  isCompleting: boolean;
  isReopening: boolean;
  isUploadingImage: boolean;
  isSubmittingExpense: boolean;

  // Handlers
  onBack?: () => void;
  onStartSettling: () => void;
  onComplete: () => void;
  onReopen: () => void;
  onEditExpense: (expense: ExpenseResponseDto) => void;
  onDeleteExpense: (expenseId: string) => void;
  onSplitAll: (expense: ExpenseResponseDto) => void;
  onUploadImage: (file: File) => Promise<SessionImageDto | undefined>;
  onRemoveImage: (imageId: string) => Promise<void>;
  onShowExpenseDialog: () => void;
  onCloseExpenseDialog: (open: boolean) => void;
  onSubmitExpense: (expense: unknown) => void;
}

/**
 * SessionDetail presenter component.
 * Pure presentation - receives all data and callbacks as props.
 * Does not contain any hooks for data fetching or mutations.
 */
export function SessionDetailPresenter({
  groupId,
  session,
  members,
  currentUserId,
  currentUserType,
  canEdit,
  simplified,
  expenses,
  balances,
  sessionImages,
  showExpenseDialog,
  editingExpense,
  isInputting,
  isSettling,
  isCompleted,
  canModify,
  isStartingSettling,
  isCompleting,
  isReopening,
  isUploadingImage,
  isSubmittingExpense,
  onBack,
  onStartSettling,
  onComplete,
  onReopen,
  onEditExpense,
  onDeleteExpense,
  onSplitAll,
  onUploadImage,
  onRemoveImage,
  onShowExpenseDialog,
  onCloseExpenseDialog,
  onSubmitExpense,
}: SessionDetailPresenterProps) {
  const { t } = useTranslation();

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  return (
    <div className="space-y-4">
      {/* Header - only show when not simplified */}
      {!simplified && onBack && (
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("sessions.backToList", "精算一覧に戻る")}
          </button>
        </div>
      )}

      {/* Session Info */}
      <div className="flex items-start justify-between rounded-lg border bg-card p-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold">{session.name}</h2>
            <Badge
              variant={isCompleted ? "secondary" : isSettling ? "outline" : "default"}
              className="text-xs"
            >
              {isCompleted
                ? t("sessions.completed", "完了")
                : isSettling
                  ? t("sessions.settling", "精算中")
                  : t("sessions.inputting", "入力中")}
            </Badge>
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
            <span>
              {t("sessions.totalExpenses", "合計出費")}:{" "}
              <span className="font-semibold text-foreground">
                {formatAmount(session.totalExpenses)}
              </span>
            </span>
            <span>·</span>
            <span>
              {session.expenseCount}
              {t("sessions.expenseCount", "件")}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {/* 入力完了ボタン: inputting状態のときのみ表示 */}
          {canEdit && isInputting && (
            <Button
              variant="outline"
              size="sm"
              className="text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-950"
              onClick={onStartSettling}
              disabled={isStartingSettling}
            >
              <Send className="mr-2 h-4 w-4" />
              {t("sessions.startSettling", "入力完了")}
            </Button>
          )}
          {/* 精算完了ボタン: settling状態のときのみ表示 */}
          {canEdit && isSettling && (
            <Button
              variant="outline"
              size="sm"
              className="text-green-600 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:hover:bg-green-950"
              onClick={onComplete}
              disabled={isCompleting}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              {t("sessions.complete", "精算完了")}
            </Button>
          )}
          {/* 再開ボタン: completed状態のときのみ表示 */}
          {canEdit && isCompleted && (
            <Button variant="outline" size="sm" onClick={onReopen} disabled={isReopening}>
              <RotateCcw className="mr-2 h-4 w-4" />
              {t("sessions.reopen", "再開")}
            </Button>
          )}
        </div>
      </div>

      {/* Settling Session Warning */}
      {isSettling && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          <Lock className="h-4 w-4 shrink-0" />
          {t(
            "sessions.settlingWarning",
            "精算中のため出費の追加・編集はできません。精算が終わったら「精算完了」をクリックしてください。"
          )}
        </div>
      )}

      {/* Completed Session Warning */}
      {isCompleted && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
          {t(
            "sessions.completedWarning",
            "この精算は完了しています。編集するには「再開」をクリックしてください。"
          )}
        </div>
      )}

      {/* Balance Summary */}
      {balances && balances.length > 0 && <BalanceSummary balances={balances} />}

      {/* Settlements */}
      <SettlementView
        groupId={groupId}
        sessionId={session.id}
        currentUserId={currentUserId}
        currentUserType={currentUserType}
        canEdit={canModify}
      />

      {/* Receipt Images */}
      {canModify && (
        <div className="rounded-lg border bg-card p-4">
          <ReceiptUploader
            images={sessionImages}
            onUpload={onUploadImage}
            onRemove={onRemoveImage}
            isUploading={isUploadingImage}
            disabled={isCompleted}
          />
        </div>
      )}

      {/* Expense List */}
      <ExpenseList
        expenses={expenses}
        members={members}
        onAddClick={onShowExpenseDialog}
        onEditClick={onEditExpense}
        onDeleteClick={onDeleteExpense}
        onSplitAll={onSplitAll}
        canEdit={canModify}
      />

      {/* Expense Form Dialog */}
      <ExpenseForm
        open={showExpenseDialog}
        onOpenChange={onCloseExpenseDialog}
        members={members}
        currentUserId={currentUserId}
        sessionId={session.id}
        editingExpense={editingExpense}
        onSubmit={onSubmitExpense}
        isSubmitting={isSubmittingExpense}
      />
    </div>
  );
}
